import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { cellFormat } from '../../../../../../lib/designSystem/Table/cellFormat';
import { formatDate } from '../../../../../../lib/functions/utils';
import { getImageDimensionsFromBase64 } from '../../../../../Finance/Components/Financing/common/reportUtils/documentUtils';

const writeContinueText = (text: string, doc: any, x: number, y: number) => {
  const textLines = doc.splitTextToSize(text, 160);
  doc.text(textLines, x, y);
};

export const generateBalancePDF = async (
  summaryData: any,
  bussinesName: string,
  businessIdentifier: string,
  imageBase64: string,
  userName: string,
  commentary?: string,
) => {
  // Create PDF in landscape mode with larger page size for more vertical space
  const doc = new jsPDF('landscape', 'mm', 'a3'); // A3 landscape: 420mm x 297mm

  // Add the image
  const imgX = 350; // X position for the image (moved right)
  const imgY = 10; // Y position for the image
  const imgHeight = 15; // Height of the image

  const { width: naturalWidth, height: naturalHeight } =
    await getImageDimensionsFromBase64(imageBase64);
  const aspectRatio = naturalWidth / naturalHeight;
  const imgWidth = imgHeight * aspectRatio;

  // Load and add the image
  doc.addImage(imageBase64 as any, 'PNG', imgX, imgY, imgWidth, imgHeight);

  // Add the title text
  doc.setFontSize(16);
  doc.text('Resumen balance', 12, 15);

  // Add company info and date/user info in two columns
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Empresa     :  ', 12, 25);
  doc.text('Fecha   : ', 120, 25);
  doc.text('Rut              :  ', 12, 30);
  doc.text('Usuario : ', 120, 30);

  doc.setFont('helvetica', 'normal');
  doc.text(bussinesName, 12 + 25, 25);
  doc.text(formatDate(new Date()), 120 + 25, 25);
  doc.text(businessIdentifier, 12 + 25, 30);
  doc.text(userName, 90 + 25, 30);

  if (commentary && commentary !== '') {
    doc.setFont('helvetica', 'bold');
    doc.text('Comentarios : ', 12, 35);
    doc.setFont('helvetica', 'normal');
    writeContinueText(commentary, doc, 36, 35);
  }

  // Start Y position after header (moved up)
  const startY = 45;

  // Create a unified table with 4 sections arranged in a 2x2 grid
  const fullTableWidth = 380; // Full width for A2 landscape (594mm - margins)
  const sectionWidth = (fullTableWidth - 20) / 2; // Width for each section (minus gap)
  const leftColumnX = 20; // Centered left column
  const rightColumnX = 20 + sectionWidth + 20; // Centered right column with gap

  const specialCells = [
    'TOTAL ACTIVOS CIRCULANTES',
    'ACTIVO FIJO NETO',
    'TOTAL ACTIVOS',
    'TOTAL PASIVOS',
    'TOTAL PASIVO CIRCULANTE',
    'TOTAL PASIVOS A LARGO PLAZO',
    'TOTAL PASIVO EXIGIBLE',
    'PATRIMONIO',
    'MARGEN DE EXPLOTACION',
    'UTILIDAD OPERACIONAL BRUTA',
    'UTILIDAD NETA',
  ];

  // Always show 4 year columns with default values
  const defaultYears = ['-', '-', '-', '-']; // Default headers
  const actualYears = summaryData.years || [];

  // Use actual years if available, otherwise use defaults
  const displayYears = actualYears.length > 0 ? actualYears : defaultYears;

  // Ensure we always have exactly 4 columns
  while (displayYears.length < 4) {
    displayYears.push('-');
  }

  // Take only the first 4 years if there are more
  const finalYears = displayYears.slice(0, 4);

  // Helper function to create a section within the unified table
  const createSection = (
    category: string,
    x: number,
    y: number,
    width: number,
  ) => {
    // Add category title
    doc.setFontSize(10);
    doc.text(category, x, y);

    const specialRowIndices: number[] = [];

    // Prepare data for this category
    const categoryRows = summaryData.data[category].map(
      (item: any, index: number) => {
        const row: any[] = [];
        // Add account name
        row.push(item.key);
        // Add year values (always 4 columns)
        finalYears.forEach((year: any, yearIndex: number) => {
          let value = '0'; // Default value
          if (year !== '-' && item[year] !== undefined) {
            if (Object.keys(item).includes('rowType')) {
              if (item.rowType === 'isFather') {
                value = cellFormat(item[year], 'money');
              } else {
                value = cellFormat(item[year], item.rowType);
              }
            } else {
              value = cellFormat(item[year], 'money');
            }
          }
          row.push(value);
        });

        // Check if this row should be styled as special
        if (specialCells.includes(item.key)) {
          specialRowIndices.push(index); // +1 because index 0 is header
        }

        return row;
      },
    );

    // Limit rows per category
    const limitedRows = categoryRows.slice(0, 50);

    // Create the section table
    autoTable(doc, {
      head: [['Cuenta', ...finalYears]],
      body: limitedRows,
      startY: y + 5,
      margin: { left: x },
      tableWidth: width,
      pageBreak: 'avoid',
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 8,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 6,
        cellPadding: 0.5,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
        overflow: 'linebreak',
        cellWidth: 'wrap',
      },
      columnStyles: {
        0: { fontStyle: 'bold', fontSize: 7, cellWidth: 80 }, // Cuenta column
        // Make all year columns narrower to accommodate more dates
        ...finalYears.reduce((acc: any, year: any, index: number) => {
          acc[index + 1] = { cellWidth: 25, halign: 'center' }; // Centered year columns
          return acc;
        }, {}),
      },
      didParseCell: function (data: any) {
        // Style cells that are in the specialCells array
        if (data.row.index > 0 && data.column.index === 0) {
          const cellValue = data.cell.text.join('');
          console.log('CELL VALUE:', cellValue);
          if (specialCells.includes(cellValue)) {
            // Apply grey color to current cell
            data.cell.styles.fillColor = [200, 200, 200]; // Darker grey background
            data.cell.styles.textColor = [50, 50, 50]; // Darker text
            data.cell.styles.fontStyle = 'bold'; // Make it bold too
          }
        }
        // Style all columns in special rows
        if (specialRowIndices.includes(data.row.index)) {
          data.cell.styles.fillColor = [200, 200, 200]; // Darker grey background
          data.cell.styles.textColor = [50, 50, 50]; // Darker text
          data.cell.styles.fontStyle = 'bold'; // Make it bold too
        }
      },
    });

    return (doc as any).lastAutoTable.finalY;
  };

  // Create the unified table structure
  // Top row: Activos and Estado de resultados
  const topRowY = startY;
  const activosEndY = createSection(
    'Activos',
    leftColumnX,
    topRowY,
    sectionWidth,
  );
  const estadoEndY = createSection(
    'Estado de resultados',
    rightColumnX,
    topRowY,
    sectionWidth,
  );

  // Bottom row: Pasivos and Métricas
  // Left column: Pasivos joins directly with Activos (no gap)
  createSection('Pasivos', leftColumnX, activosEndY + 5, sectionWidth);
  // Right column: Métricas has a small gap from Estado de resultados
  createSection('Métricas', rightColumnX, estadoEndY + 7, sectionWidth);

  // Save the PDF
  doc.save(`Resumen Balance ${bussinesName}.pdf`);
};
